import {
  Box,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { SurveySettingsEnums } from "@pulse/pulse-rpcs/lib/types/surveySettings";
import { SingleChoiceAnswerV1 } from "@pulse/pulse-rpcs/lib/types/singleChoiceAnswerV1";
import {
  EMPTY_JSON,
  RGG_PALETTE_COLORS_FOR_SLIDER,
  RICH_TEXT_DEFAULT_FONT_COLOR,
  RICH_TEXT_MARKDOWN_PROPS,
  SingleChoiceDisplayTypeV1Enum,
  createSingleSelectQuestionV1Model,
  getDarkGradientColorForSliderByIndexPosition,
  getLightGradientColorForIndexPosition,
  isBrowserLightMode,
  richTextTypography,
} from "@pulse/shared-components";
import {
  useCornerRadius,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { RichTextEditor } from "@surya-digital/leo-reactjs-remirror";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SingleSelectQuestionV1Model } from "@pulse/shared-components";

const DESKTOP_BREAKPOINT = "(min-width:1200px)";

interface ChatSingleSelectQuestionBodyProps {
  questionDetailsJson: string;
  draftAnswerJson?: string;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  dropdownPlaceholder: string;
  onAnswerChange: (answerJson: string | null) => void;
}

interface ChatSingleSelectAnswerTextProps {
  questionDetailsJson: string;
  answerJson: string;
}

const isAnswerPresent = (answerJson: string): boolean =>
  answerJson !== EMPTY_JSON;

const createQuestionModel = (
  questionDetailsJson: string,
  answerJson?: string,
): Instance<typeof SingleSelectQuestionV1Model> => {
  const model = createSingleSelectQuestionV1Model();
  model.deserializeQuestionJSON(questionDetailsJson);
  if (answerJson && isAnswerPresent(answerJson)) {
    const savedAnswer = SingleChoiceAnswerV1.fromDTO(JSON.parse(answerJson));
    const localizedAnswer =
      model.optionTextFromCode(savedAnswer.code.code) ??
      (model.NAOption.naOptionCode === savedAnswer.code.code
        ? model.NAOption.naOptionString
        : savedAnswer.answer);
    model.deserializeAnswerJSON(
      JSON.stringify({
        ...JSON.parse(answerJson),
        answer: localizedAnswer,
      }),
    );
  }
  return model;
};

const ChatOptionText = ({
  text,
  fontFamily,
  fontScale,
  isBigScreen,
  color,
}: {
  text: string | null | undefined;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  isBigScreen: boolean;
  color: string;
}): React.ReactElement | null => {
  if (!text) return null;

  return (
    <Box
      sx={{
        color,
        minWidth: 0,
        "& .ProseMirror, & .ProseMirror *": {
          color: `${color} !important`,
          overflowWrap: "anywhere",
        },
      }}
    >
      <RichTextEditor
        name="chatSingleSelectOption"
        initialValue={text}
        typography={richTextTypography(
          fontFamily,
          fontScale,
          RICH_TEXT_DEFAULT_FONT_COLOR,
          isBigScreen,
        )}
        mode="preview"
        supports={RICH_TEXT_MARKDOWN_PROPS}
        borderOnPreview={false}
      />
    </Box>
  );
};

/**
 * Chat-only single-select interaction. The question and answer bubbles remain
 * owned by the chat timeline; this component contains only the response input.
 */
export const ChatSingleSelectQuestionBody = observer(
  ({
    questionDetailsJson,
    draftAnswerJson,
    fontFamily,
    fontScale,
    dropdownPlaceholder,
    onAnswerChange,
  }: ChatSingleSelectQuestionBodyProps): React.ReactElement => {
    const tokens = useFoundationColorTokens();
    const spacing = useSpacing();
    const typography = useTypography();
    const radius = useCornerRadius();
    const { t } = useTranslation();
    const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT);
    const isDarkMode = !isBrowserLightMode();
    const optionBorderColor = `var(--chat-group-option-border-color, ${tokens.border})`;
    const model = useMemo(
      () => createQuestionModel(questionDetailsJson, draftAnswerJson),
      [draftAnswerJson, questionDetailsJson],
    );

    const publishAnswer = useCallback((): void => {
      const answerJson = model.serializedAnswerJSON;
      onAnswerChange(isAnswerPresent(answerJson) ? answerJson : null);
    }, [model, onAnswerChange]);

    useEffect(() => {
      publishAnswer();
    }, [publishAnswer]);

    const selectOption = (code: string, answer: string | null): void => {
      model.setIsNotApplicableAnswerSelected(false);
      model.setSelectedAnswer(code, answer);
      publishAnswer();
    };

    const selectNotApplicable = (): void => {
      model.setIsNotApplicableAnswerSelected(true);
      model.setSliderValue(undefined);
      model.setSelectedAnswer(
        model.NAOption.naOptionCode,
        model.NAOption.naOptionString,
      );
      publishAnswer();
    };

    const optionBorder = (selected: boolean): string =>
      selected
        ? `2px solid ${tokens.borderPrimary}`
        : `1px solid ${optionBorderColor}`;

    const RadioButtons = (): React.ReactElement => (
      <Stack gap={spacing.spaceSM} width="100%">
        {model.options.map((option) => {
          const selected = model.isOptionSelected(option.code);
          return (
            <Stack
              key={option.code}
              component="label"
              direction="row"
              alignItems="center"
              gap={spacing.spaceXS}
              minHeight="44px"
              padding={`${spacing.spaceXS} ${spacing.spaceSM}`}
              bgcolor={tokens.backgroundElevatedLevel1}
              border={optionBorder(selected)}
              borderRadius={radius.radiusXS}
              textAlign="left"
              onClick={(): void => selectOption(option.code, option.option)}
              sx={{
                cursor: "pointer",
                font: "inherit",
                "&:hover": {
                  backgroundColor: selected
                    ? tokens.backgroundPrimarySubtle
                    : tokens.backgroundHovered,
                },
              }}
            >
              <Radio
                checked={selected}
                value={option.code}
                disableRipple
                inputProps={{ "aria-label": option.option || option.code }}
                sx={{
                  padding: 0,
                  color: tokens.iconPrimary,
                  "&.Mui-checked": { color: tokens.iconPrimary },
                }}
              />
              <ChatOptionText
                text={option.option}
                fontFamily={fontFamily}
                fontScale={fontScale}
                isBigScreen={isDesktop}
                color={tokens.label}
              />
            </Stack>
          );
        })}
      </Stack>
    );

    const Cards = (): React.ReactElement => {
      const horizontal = isDesktop && model.isHorizontal;
      return (
        <Stack
          direction={horizontal ? "row" : "column"}
          flexWrap="wrap"
          gap={spacing.spaceSM}
          width="100%"
        >
          {model.options.map((option) => {
            const selected = model.isOptionSelected(option.code);
            const textAlign = model.isRightAligned
              ? "right"
              : model.isCenterAligned
                ? "center"
                : "left";
            return (
              <Stack
                key={option.code}
                component="button"
                type="button"
                role="radio"
                aria-checked={selected}
                minHeight={horizontal ? "56px" : "44px"}
                flex={horizontal ? "1 1 220px" : undefined}
                padding={`${spacing.spaceSM} ${spacing.spaceMD}`}
                bgcolor={
                  selected
                    ? tokens.backgroundPrimarySubtle
                    : tokens.backgroundElevatedLevel1
                }
                border={optionBorder(selected)}
                borderRadius={radius.radiusXS}
                justifyContent="center"
                textAlign={textAlign}
                onClick={(): void => selectOption(option.code, option.option)}
                sx={{
                  cursor: "pointer",
                  font: "inherit",
                  "&:hover": {
                    backgroundColor: selected
                      ? tokens.backgroundPrimarySubtle
                      : tokens.backgroundHovered,
                  },
                  "& .ProseMirror": { textAlign },
                }}
              >
                <ChatOptionText
                  text={option.option}
                  fontFamily={fontFamily}
                  fontScale={fontScale}
                  isBigScreen={isDesktop}
                  color={selected ? tokens.labelPrimary : tokens.label}
                />
              </Stack>
            );
          })}
        </Stack>
      );
    };

    const Dropdown = (): React.ReactElement => (
      <Select
        fullWidth
        displayEmpty
        value={model.selectedAnswer?.code ?? ""}
        onChange={(event: SelectChangeEvent<string>): void => {
          const option = model.options.find(
            ({ code }) => code === event.target.value,
          );
          if (option) selectOption(option.code, option.option);
        }}
        inputProps={{ "aria-label": dropdownPlaceholder }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: tokens.backgroundElevatedLevel2,
              border: `1px solid ${tokens.borderSubtle}`,
              color: tokens.label,
              maxHeight: "240px",
              "& .MuiMenuItem-root": {
                color: tokens.label,
                "&:hover": {
                  backgroundColor: tokens.backgroundHoveredElevated,
                },
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: tokens.backgroundSelected,
                },
              },
            },
          },
        }}
        renderValue={(value): React.ReactNode => {
          if (!value) {
            return (
              <Typography sx={{ ...typography.b2, color: tokens.labelSubtle }}>
                {dropdownPlaceholder}
              </Typography>
            );
          }
          return (
            <ChatOptionText
              text={model.optionTextFromCode(value) ?? value}
              fontFamily={fontFamily}
              fontScale={fontScale}
              isBigScreen={isDesktop}
              color={tokens.label}
            />
          );
        }}
        sx={{
          backgroundColor: tokens.backgroundElevatedLevel1,
          borderRadius: radius.radiusXS,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.border,
          },
          "&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              borderColor: tokens.borderPrimary,
            },
        }}
      >
        {model.options.map((option) => (
          <MenuItem key={option.code} value={option.code}>
            <ChatOptionText
              text={option.option}
              fontFamily={fontFamily}
              fontScale={fontScale}
              isBigScreen={isDesktop}
              color={tokens.label}
            />
          </MenuItem>
        ))}
      </Select>
    );

    const Sliders = (): React.ReactElement => {
      const selectedValue = model.selectedSliderValue;
      const isRyg = model.isRygSlider;
      const maxSliderValue = Math.max(model.options.length - 1, 0);
      const sliderValue = selectedValue ?? model.defaultSliderOption;
      const sliderPalette = isDarkMode
        ? RGG_PALETTE_COLORS_FOR_SLIDER.LEFT_MOST.darkMode
        : RGG_PALETTE_COLORS_FOR_SLIDER.LEFT_MOST.lightMode;
      const sliderMiddlePalette = isDarkMode
        ? RGG_PALETTE_COLORS_FOR_SLIDER.MIDDLE.darkMode
        : RGG_PALETTE_COLORS_FOR_SLIDER.MIDDLE.lightMode;
      const sliderRightPalette = isDarkMode
        ? RGG_PALETTE_COLORS_FOR_SLIDER.RIGHT_MOST.darkMode
        : RGG_PALETTE_COLORS_FOR_SLIDER.RIGHT_MOST.lightMode;
      const selectedRygColor = getDarkGradientColorForSliderByIndexPosition(
        sliderValue,
        maxSliderValue,
        isDarkMode,
      );
      const endpointText = (index: number): string =>
        model.options[index]?.option || model.options[index]?.code || "";
      const selectedOptionLabel =
        selectedValue !== undefined &&
        selectedValue !== 0 &&
        selectedValue !== maxSliderValue
          ? endpointText(selectedValue)
          : undefined;
      const selectedOptionPosition =
        selectedValue !== undefined && maxSliderValue > 0
          ? (selectedValue / maxSliderValue) * 100
          : undefined;
      return (
        <Stack
          gap={spacing.spaceXS}
          width="100%"
          paddingX="12px"
          sx={{ boxSizing: "border-box" }}
        >
          <Stack
            minHeight={isDesktop ? "28px" : "20px"}
            paddingBottom={isDesktop ? spacing.spaceXS : 0}
            position="relative"
            width="100%"
          >
            {selectedOptionLabel && selectedOptionPosition !== undefined && (
              <Typography
                sx={{
                  ...typography.b2,
                  color: tokens.label,
                  left: `${selectedOptionPosition}%`,
                  maxWidth: "100%",
                  overflowWrap: "anywhere",
                  position: "absolute",
                  textAlign: "center",
                  transform: "translateX(-50%)",
                }}
              >
                {selectedOptionLabel}
              </Typography>
            )}
          </Stack>
          <Slider
            min={0}
            max={maxSliderValue}
            step={1}
            value={sliderValue}
            marks={model.options.map((_, index) => ({ value: index }))}
            track={selectedValue === undefined ? false : "normal"}
            onChange={(_event, value): void => {
              if (typeof value !== "number") return;
              model.setIsNotApplicableAnswerSelected(false);
              model.setSliderValue(value);
              publishAnswer();
            }}
            aria-label={t("surveys.chatInterface.selectRatingAriaLabel")}
            sx={{
              color: tokens.backgroundPrimary,
              height: "16px",
              padding: 0,
              "& .MuiSlider-rail": {
                opacity: 1,
                background: isRyg
                  ? `linear-gradient(90deg, ${sliderPalette.unselected}, ${sliderMiddlePalette.unselected}, ${sliderRightPalette.unselected})`
                  : tokens.backgroundSubtle,
              },
              "& .MuiSlider-track": {
                border: 0,
                background: isRyg
                  ? `linear-gradient(90deg, ${sliderPalette.selected}, ${sliderMiddlePalette.selected}, ${selectedRygColor})`
                  : tokens.backgroundPrimary,
              },
              "& .MuiSlider-mark": {
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                // MUI's default offset is sized for its 2px marks.
                transform: "translate(-50%, -50%)",
                backgroundColor: isRyg
                  ? tokens.backgroundInverse
                  : tokens.backgroundLowEmphasis,
              },
              ...(maxSliderValue > 0
                ? {
                    '& .MuiSlider-mark[data-index="0"]': {
                      transform: "translate(0, -50%)",
                    },
                    [`& .MuiSlider-mark[data-index="${maxSliderValue}"]`]: {
                      transform: "translate(-100%, -50%)",
                    },
                  }
                : {}),
              "& .MuiSlider-markActive": {
                backgroundColor: tokens.backgroundElevatedLevel1,
              },
            }}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            gap={spacing.spaceMD}
          >
            <Typography sx={{ ...typography.b2, color: tokens.labelSubtle }}>
              {endpointText(0)}
            </Typography>
            <Typography
              textAlign="right"
              sx={{ ...typography.b2, color: tokens.labelSubtle }}
            >
              {endpointText(model.options.length - 1)}
            </Typography>
          </Stack>
          {model.NAOption.isNAOptionChecked && (
            <Stack
              component="button"
              type="button"
              alignSelf="flex-start"
              padding={`${spacing.spaceXS} ${spacing.spaceSM}`}
              border={optionBorder(model.isNotApplicableAnswerSelected)}
              borderRadius={radius.radiusXS}
              bgcolor={tokens.backgroundElevatedLevel1}
              onClick={selectNotApplicable}
              sx={{ cursor: "pointer", font: "inherit" }}
            >
              <ChatOptionText
                text={model.NAOption.naOptionString}
                fontFamily={fontFamily}
                fontScale={fontScale}
                isBigScreen={isDesktop}
                color={tokens.label}
              />
            </Stack>
          )}
        </Stack>
      );
    };

    const Nps = (): React.ReactElement => {
      const amberUnselected = isDarkMode ? "#5A3C00" : "#FFDE9A";
      const amberSelected = isDarkMode ? "#FFBE3D" : "#FFAA00";
      const coloredBackgroundFor = (
        index: number,
        selected: boolean,
      ): string => {
        if (index <= 6) {
          return selected
            ? tokens.backgroundError
            : isDarkMode
              ? tokens.backgroundErrorSubtle
              : "#EFB7BC";
        }
        if (index <= 8) return selected ? amberSelected : amberUnselected;
        return selected
          ? tokens.backgroundSuccess
          : isDarkMode
            ? tokens.backgroundSuccessSubtle
            : "#A1CEB2";
      };
      const backgroundFor = (index: number, selected: boolean): string => {
        if (selected) {
          if (model.isNPSSelectionColored) {
            return coloredBackgroundFor(index, true);
          }
          return tokens.backgroundPrimary;
        }
        if (model.isNpsPreFilledColor) {
          return coloredBackgroundFor(index, false);
        }
        return model.isNpsBorderColor
          ? tokens.backgroundElevatedLevel1
          : tokens.backgroundSubtle;
      };
      const borderFor = (
        index: number,
        selected: boolean,
      ): string | undefined => {
        if (!model.isNpsBorderColor || selected) return undefined;
        if (index <= 6) return `2px solid ${tokens.borderErrorSubtle}`;
        if (index <= 8) return `2px solid ${amberUnselected}`;
        return `2px solid ${tokens.borderSuccessSubtle}`;
      };
      return (
        <Stack gap={spacing.spaceSM} width="100%">
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="center"
            gap={spacing.spaceSM}
          >
            {model.options.map((option, index) => {
              const selected = model.isOptionSelected(option.code);
              return (
                <Box
                  key={option.code}
                  component="button"
                  type="button"
                  aria-pressed={selected}
                  width={isDesktop ? "52px" : "40px"}
                  height={isDesktop ? "52px" : "40px"}
                  borderRadius="50%"
                  border={borderFor(index, selected)}
                  bgcolor={backgroundFor(index, selected)}
                  color={selected ? tokens.labelOnSurface : tokens.label}
                  onClick={(): void => selectOption(option.code, option.option)}
                  sx={{ cursor: "pointer", font: "inherit", fontWeight: 600 }}
                >
                  {option.code}
                </Box>
              );
            })}
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            gap={spacing.spaceMD}
          >
            <ChatOptionText
              text={model.options[0]?.option}
              fontFamily={fontFamily}
              fontScale={fontScale}
              isBigScreen={isDesktop}
              color={tokens.labelSubtle}
            />
            <ChatOptionText
              text={model.options[model.options.length - 1]?.option}
              fontFamily={fontFamily}
              fontScale={fontScale}
              isBigScreen={isDesktop}
              color={tokens.labelSubtle}
            />
          </Stack>
        </Stack>
      );
    };

    const RatingScale = (): React.ReactElement => {
      const neutral = model.isNeutralScale;
      const colorFor = (index: number): string =>
        getLightGradientColorForIndexPosition(
          index,
          model.options.length,
          isDarkMode,
        );
      return (
        <Stack gap={spacing.spaceSM} width="100%">
          <Stack direction="column" gap={0}>
            {model.options.map((option, index) => {
              const selected = model.isOptionSelected(option.code);
              const label = option.option;
              return (
                <Stack
                  key={option.code}
                  component="button"
                  type="button"
                  flex="1 1 0"
                  minHeight="52px"
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  padding={`${spacing.spaceXS} ${spacing.spaceSM}`}
                  border={`1px solid ${optionBorderColor}`}
                  borderLeft={
                    neutral ? undefined : `8px solid ${colorFor(index)}`
                  }
                  borderRadius={0}
                  bgcolor={
                    selected
                      ? neutral
                        ? tokens.backgroundPrimary
                        : colorFor(index)
                      : tokens.backgroundElevatedLevel1
                  }
                  onClick={(): void => selectOption(option.code, option.option)}
                  sx={{ cursor: "pointer", font: "inherit" }}
                >
                  <ChatOptionText
                    text={label}
                    fontFamily={fontFamily}
                    fontScale={fontScale}
                    isBigScreen={isDesktop}
                    color={
                      selected && neutral ? tokens.labelOnSurface : tokens.label
                    }
                  />
                </Stack>
              );
            })}
          </Stack>
          {model.NAOption.isNAOptionChecked && (
            <Stack
              component="button"
              type="button"
              minHeight="52px"
              alignItems="center"
              justifyContent="center"
              padding={`${spacing.spaceXS} ${spacing.spaceSM}`}
              border={optionBorder(model.isNotApplicableAnswerSelected)}
              borderRadius={radius.radiusXS}
              bgcolor={tokens.backgroundElevatedLevel1}
              onClick={selectNotApplicable}
              sx={{ cursor: "pointer", font: "inherit" }}
            >
              <ChatOptionText
                text={model.NAOption.naOptionString}
                fontFamily={fontFamily}
                fontScale={fontScale}
                isBigScreen={isDesktop}
                color={tokens.label}
              />
            </Stack>
          )}
        </Stack>
      );
    };

    switch (model.singleChoiceDisplayType) {
      case SingleChoiceDisplayTypeV1Enum.RadioButton:
        return <RadioButtons />;
      case SingleChoiceDisplayTypeV1Enum.Card:
        return <Cards />;
      case SingleChoiceDisplayTypeV1Enum.Dropdown:
        return <Dropdown />;
      case SingleChoiceDisplayTypeV1Enum.DefaultSlider:
      case SingleChoiceDisplayTypeV1Enum.RygSlider:
        return <Sliders />;
      case SingleChoiceDisplayTypeV1Enum.DefaultNps:
      case SingleChoiceDisplayTypeV1Enum.NpsPreFilledColor:
      case SingleChoiceDisplayTypeV1Enum.NpsBorderColor:
        return <Nps />;
      case SingleChoiceDisplayTypeV1Enum.DefaultScale:
      case SingleChoiceDisplayTypeV1Enum.ScaleWithText:
      case SingleChoiceDisplayTypeV1Enum.NeutralScale:
        return <RatingScale />;
      default:
        return <></>;
    }
  },
);

export const ChatSingleSelectAnswerText = ({
  questionDetailsJson,
  answerJson,
}: ChatSingleSelectAnswerTextProps): React.ReactElement | null => {
  try {
    const model = createQuestionModel(questionDetailsJson);
    const answer = SingleChoiceAnswerV1.fromDTO(JSON.parse(answerJson));
    const answerText = answer.answer?.trim();
    const optionText = model.optionTextFromCode(answer.code.code)?.trim();
    const naText =
      model.NAOption.naOptionCode === answer.code.code
        ? model.NAOption.naOptionString.trim()
        : undefined;
    const text =
      [optionText, naText, answerText].find((label) => label?.length) ??
      answer.code.code;
    return <>{text}</>;
  } catch {
    return null;
  }
};
